import {
  APIComponentAttributes,
  PartialBulkUpdateValues,
  ReportColumnType,
} from '@ardoq/api-types';
import {
  type ArdoqError,
  formatApiErrorWithTraceId,
  getArdoqErrorTraceId,
  isArdoqError,
  isTruthy,
} from '@ardoq/common-helpers';
import { EnhancedScopeData } from '@ardoq/data-model';
import { showToast, ToastType } from '@ardoq/status-ui';
import { confirm, isConfirmResult, ModalSize } from '@ardoq/modal';
import { t } from '@ardoq/i18n';
import {
  CellKeyDownEvent,
  CellValueChangedEvent,
  ColumnMenuVisibleChangedEvent,
  ColumnPinnedEvent,
  DisplayedColumnsChangedEvent,
  FillEndEvent,
  FilterChangedEvent,
  GridApi,
  IRowNode,
  PasteEndEvent,
  PasteStartEvent,
  ProcessCellForExportParams,
  ProcessDataFromClipboardParams,
  RowDragEvent,
  SelectionChangedEvent,
  SortChangedEvent,
} from 'ag-grid-enterprise';
import { isEmpty, isEqual, omit, partition, uniq } from 'lodash';
import { tagOperations, PendingTagChange } from '@ardoq/core';
import { deduplicateRoutes, getNodeRoute } from './agGridRouteOperations';
import { refreshServerSideForGrid } from './serverSideRefresh';
import { cellValueChangedEventOperations } from './updateComponents/cellValueChangedEventOperations';
import { columnOperations } from './columns/columnOperations';
import { AG_GRID_SELECTION_COLUMN_ID } from './columns/selectAndDeleteColumnDefinition';
import { createComponentsOperations } from './createComponents/createComponentsOperations';
import { deleteComponentColumnId } from './createComponents/getDeleteColumnDefinition';
import { insertNewComponentRow } from './createComponents/insertNewRowsInAgGrid';
import { mapFilterModelToSubquery } from './filters/mapFilterModelToSubquery';
import { FilterModel } from './filters/types';
import { inventoryGridAPIOperations } from './inventoryGridAPIOperations';
import { hierarchyInAgGridOperations } from './hierarchy/hierarchyInAgGridOperations';
import { reportComponentRowsOperations } from './reportComponentRowsOperations';
import {
  CreateComponentParam,
  BulkEditEventSource,
  ExtendedRowNode,
  InventoryContext,
  InventoryReferenceRepresentation,
  MultiUpdateEventSource,
  PendingLucidchartChange,
  PendingReferenceChange,
  SavingStatus,
  ColumnDefinitionArdoqMetadata,
} from './types';
import {
  getBulkReferenceDeleteConfirmationDialog,
  getNothingChangedDialog,
  getUnresolvedNamesDialog,
  getMultiUpdateConfirmationDialog,
  getConfirmMultiUpdateFailedDialog,
  getConfirmCannotMoveComponentsDialog,
} from './updateComponents/confirmDialogs';
import { getMoveComponentsDialogText } from './updateComponents/moveComponentsDialogContent';
import {
  clearPendingReferenceChangesFromNodes,
  collectPendingReferenceChangesFromNodes,
  loadScopeDataForPendingReferences,
  editReferencesSingleComponent,
  multiUpdateReferences,
  resolveReferenceChanges,
  ResolvedReferenceChanges,
  showEditReferencesFailedDialog,
  toPendingReferenceChange,
} from './updateComponents/editReferences';
import {
  clearPendingComponentNamesFromNodes,
  resolvePastedComponentNames,
} from './updateComponents/resolvePastedNames';
import {
  clearPendingLucidchartChangesFromNodes,
  collectPendingLucidchartChangesFromNodes,
  editLucidchartDocumentsSingleComponent,
  LucidchartCellValueChangedEvent,
  markNodeAsPendingLucidchartChange,
  multiUpdateLucidchartDocuments,
} from './updateComponents/editLucidchartDocuments';
import {
  multiUpdateTags,
  saveTags,
  collectPendingTagChangesFromNodes,
  clearPendingTagChangesFromNodes,
} from './updateComponents/saveTags';
import {
  validateComponent,
  validateNewComponent,
} from './updateComponents/validation';
import { dateRangeOperations } from '@ardoq/date-range';
import { mergeScopeDataIntoInventoryContext } from './mergeScopeDataIntoInventoryContext';
import { fromClipboardCellText, toClipboardCellText } from './valueHandlers';

const onCellKeyDown = async (cellKeyDownEvent: CellKeyDownEvent) => {
  const { event, rowPinned, api: gridApi, context } = cellKeyDownEvent;
  const isEnterPressed = event
    ? (event as KeyboardEvent).key === 'Enter'
    : false;
  const isEnterPressedFromNewComponentsCell = rowPinned === 'top';

  const currentNode = cellKeyDownEvent.node as ExtendedRowNode;
  if (
    isEnterPressed &&
    isEnterPressedFromNewComponentsCell &&
    currentNode._hasError
  ) {
    const newComponent = cellKeyDownEvent.data as APIComponentAttributes;
    if ((newComponent?.name ?? '').trim() !== '') {
      removeHasErrorFromNode(currentNode);
      await saveNewComponent({
        api: gridApi,
        node: cellKeyDownEvent.node,
        data: newComponent,
        context: context as InventoryContext,
      });
    }
  }

  const isDeletePressed = event
    ? (event as KeyboardEvent).key === 'Delete' ||
      (event as KeyboardEvent).key === 'Backspace'
    : false;
  const isCellRangeSelected = (gridApi.getCellRanges()?.length ?? 0) > 0;

  if (isDeletePressed && isCellRangeSelected) {
    const multiUpdatePayloads = getMultiUpdatePayloads(gridApi);

    await displayConfirmationDialogAndUpdateComponentsOrRevertChanges(
      multiUpdatePayloads,
      context,
      gridApi,
      'delete'
    );
  }
};

const insertNewComponentRowWithDefaultValuesFromLatestPinnedRow = (
  gridApi: GridApi
) => {
  const mostRecentPinnedRow = getLatestComponentDataFromPinnedRows(gridApi);
  if (!mostRecentPinnedRow) {
    return;
  }

  insertNewComponentRow(gridApi, {
    workspaceId: mostRecentPinnedRow.rootWorkspace,
    componentTypeId: mostRecentPinnedRow.typeId,
    modelId: mostRecentPinnedRow.model,
  });
};

/**
 * Returns the current live data from all pinned top row nodes.
 *
 * NOTE: Do NOT use `gridApi.getGridOption('pinnedTopRowData')` here.
 * That option only reflects the last value passed to `setGridOption('pinnedTopRowData', ...)`.
 * When the user edits a cell in an unsaved pinned row (e.g. changes the component type without
 * providing a name), AG Grid updates the row node's data internally but does NOT update the
 * stored grid option. Reading from the option would return stale data, causing those edits to
 * be silently overwritten when a sibling row is saved and `setGridOption` is called again.
 */
const getCurrentPinnedTopRowsData = (
  gridApi: GridApi
): APIComponentAttributes[] => {
  const count = gridApi.getPinnedTopRowCount();
  const rows: APIComponentAttributes[] = [];
  for (let i = 0; i < count; i++) {
    const node = gridApi.getPinnedTopRow(i);
    if (node?.data) rows.push(node.data as APIComponentAttributes);
  }
  return rows;
};

const getLatestComponentDataFromPinnedRows = (gridApi: GridApi) => {
  const pinnedRows = getCurrentPinnedTopRowsData(gridApi);
  if (pinnedRows.length === 0) {
    return null;
  }
  return pinnedRows[0];
};

const setHasErrorInNode = (node: ExtendedRowNode) => {
  if (node.data) {
    node._hasError = true;
  }
};

const setHasErrorForCellAndRedrawRow = (
  node: ExtendedRowNode,
  columnId: string,
  gridApi: GridApi,
  errorMessage: string
) => {
  if (!node.data) {
    return;
  }
  node._hasError = true;
  if (!node._cellErrors) {
    node._cellErrors = {};
  }
  node._cellErrors[columnId] = errorMessage;
  gridApi.redrawRows({
    rowNodes: [node],
  });
};

const clearCellErrorAndRedrawRow = (
  node: ExtendedRowNode,
  columnId: string,
  gridApi: GridApi
) => {
  if (node._cellErrors) {
    delete node._cellErrors[columnId];
  }
  if (isEmpty(Object.values(node._cellErrors ?? {}).filter(isTruthy))) {
    removeHasErrorFromNode(node);
  }
  gridApi.redrawRows({
    rowNodes: [node],
  });
};

const clearAllCellErrorsAndRedrawRow = (
  node: ExtendedRowNode,
  gridApi: GridApi
) => {
  removeHasErrorFromNode(node);
  node._cellErrors = {};
  gridApi.redrawRows({ rowNodes: [node] });
};

const removeHasErrorFromNode = (node: ExtendedRowNode) => {
  if (node.data) {
    node._hasError = false;
  }
};

const removeRecentlySavedStatusFromNewComponents = (
  gridApi: GridApi<APIComponentAttributes>
) => {
  const pinnedTopRowCount = gridApi.getPinnedTopRowCount();
  const updatedRowNodes = [];
  for (let i = 0; i < pinnedTopRowCount; i++) {
    const rowNode = gridApi.getPinnedTopRow(i) as ExtendedRowNode | undefined;
    if (rowNode && rowNode._savingStatus) {
      updatedRowNodes.push(rowNode);
      rowNode._savingStatus = undefined;
    }
  }

  gridApi.refreshCells({
    rowNodes: updatedRowNodes,
    force: true,
    columns: ['name'],
  });
};

const replaceUnsavedComponentWithSavedOne = ({
  savedComponent,
  temporaryId,
  gridApi,
}: {
  savedComponent: APIComponentAttributes;
  temporaryId: string;
  gridApi: GridApi;
}) => {
  const previousPinnedRows = getCurrentPinnedTopRowsData(gridApi);
  if (previousPinnedRows.length === 0) {
    // it's possible that user has already exited the create component mode while we were saving the component
    // no need to insert new rows
    return;
  }

  // AG Grid does not allow updating _id property, so we need to remove the old row and insert a new one
  const previousPinnedRowsWithoutNewComponent = previousPinnedRows.filter(
    it => it._id !== temporaryId
  );
  const [emptyRows, nonEmptyPreviousPinnedRows] = partition(
    previousPinnedRowsWithoutNewComponent,
    row => createComponentsOperations.isComponentNotPersisted(row._id)
  );

  const context = gridApi.getGridOption('context') as InventoryContext;
  const scopeData = context.scopeData;

  const savedComponentInAgGridFormat =
    reportComponentRowsOperations.mapComponentFromApiComponentFormat(
      savedComponent,
      scopeData
    );

  gridApi.setGridOption('pinnedTopRowData', [
    ...emptyRows,
    savedComponentInAgGridFormat,
    ...nonEmptyPreviousPinnedRows,
  ]);

  const newRowNode = gridApi.getPinnedTopRow(
    emptyRows.length
  ) as ExtendedRowNode;
  newRowNode._savingStatus = SavingStatus.RECENTLY_SAVED;

  setTimeout(() => {
    removeRecentlySavedStatusFromNewComponents(gridApi);
  }, 2000);

  // TODO AM figure out what should happen with scope data
};

type SaveNewComponentEvent = {
  api: GridApi<APIComponentAttributes>;
  node: IRowNode<APIComponentAttributes>;
  data: APIComponentAttributes;
  context: InventoryContext;
};

const saveNewComponent = async (event: SaveNewComponentEvent) => {
  const { api: gridApi, data: newComponent, context } = event;
  const { commands } = context;
  const node = event.node as ExtendedRowNode;
  if (
    (newComponent.name ?? '').trim() === '' ||
    node._savingStatus === SavingStatus.REQUEST_SENT
  ) {
    return;
  }

  if (
    node._hasError // cannot save a component with validation errors
  ) {
    node._savingStatus = undefined;
    return;
  }

  // A multi-cell paste is processed cell-by-cell. The save is triggered as soon as the
  // valid `name` cell changes, but the row may already hold invalid values (workspace, type,
  // parent, or any other field) pasted from the same row that haven't been validated yet.
  // Validate the whole row before sending to the backend.
  const validationErrors = validateNewComponent({
    component: newComponent,
    context,
  });
  if (validationErrors.length > 0) {
    node._savingStatus = undefined;
    validationErrors.forEach(({ columnId, errorMessage }) =>
      setHasErrorForCellAndRedrawRow(node, columnId, gridApi, errorMessage)
    );
    return;
  }

  const newComponentWithSplitDateRanges =
    dateRangeOperations.splitFieldsOnEntity(
      newComponent,
      newComponent.rootWorkspace,
      context.scopeData
    );

  const createdComponentAttributes: CreateComponentParam = {
    ...omit(newComponentWithSplitDateRanges, ['_id']),
    typeId: newComponent.typeId,
    model: newComponent.model,
    rootWorkspace: newComponent.rootWorkspace,
    name: newComponent.name,
  };
  node._savingStatus = SavingStatus.REQUEST_SENT;
  const response = await commands.createComponent(createdComponentAttributes);

  if (isArdoqError(response)) {
    node._savingStatus = undefined;
    setHasErrorInNode(node);
    gridApi.redrawRows({ rowNodes: [node] });
    // Note on locale handling for imperative dialogs/toasts in this file and
    // sibling AG Grid callbacks (editReferencesSingleComponent, inventoryGridAPI):
    // imperative confirm/showToast invocations triggered by AG Grid callbacks
    // read the locale from the AG Grid context (kept in sync via a useEffect
    // in Inventory.tsx / CreateComponentsInventory.tsx). This is the
    // documented external-lib-callback exception to the
    // "no i18nLocale$.state" rule.
    showToast(
      formatApiErrorWithTraceId(
        response,
        context.i18nLocale,
        t(
          context.i18nLocale,
          'inventory:createComponent.errorToast',
          "We couldn't create the component."
        )
      ),
      ToastType.ERROR
    );
    return;
  }

  replaceUnsavedComponentWithSavedOne({
    savedComponent: response,
    temporaryId: newComponent._id,
    gridApi,
  });
  insertNewComponentRowWithDefaultValuesFromLatestPinnedRow(gridApi);
};

/*
 * Checks if the row has been removed while value edit was in progress.
 */
const hasRowBeenRemoved = (
  event: CellValueChangedEvent<APIComponentAttributes>
) => {
  return event.rowIndex === null;
};

const markNodeAsPendingMultiUpdate = (
  event: CellValueChangedEvent<APIComponentAttributes>
) => {
  if (event.node.data && event.newValue !== event.oldValue) {
    const node = event.node as ExtendedRowNode;

    const partialUpdateValues =
      cellValueChangedEventOperations.getValuesForPartialUpdate(event);

    if (!node._pendingChanges) {
      node._pendingChanges = [];
    }

    // Append the new changes to existing pending changes
    // Note: If the same field is changed multiple times, we'll have multiple entries
    // The getMultiUpdatePayloads function will handle deduplication
    node._pendingChanges.push(...partialUpdateValues);
  }
};

const markNodeAsPendingTagChange = (
  event: CellValueChangedEvent<
    APIComponentAttributes,
    string[],
    InventoryContext
  >
) => {
  const component = event.data;
  if (!component?._id || isEqual(event.oldValue ?? [], event.newValue ?? [])) {
    return;
  }

  const { tagsToAdd, tagsToRemove } = tagOperations.diffTagNames(
    event.newValue ?? [],
    event.oldValue ?? []
  );

  (event.node as ExtendedRowNode)._pendingTagChange = {
    kind: 'component',
    entityId: component._id,
    workspaceId: component.rootWorkspace,
    tagsToAdd,
    tagsToRemove,
  };
};

const markNodeAsPendingReferenceChange = (
  event: CellValueChangedEvent<
    APIComponentAttributes,
    InventoryReferenceRepresentation[],
    InventoryContext
  >
) => {
  const pendingChange = toPendingReferenceChange(event);
  if (!pendingChange) {
    return;
  }
  const node = event.node as ExtendedRowNode;
  node._pendingReferenceChanges = [
    ...(node._pendingReferenceChanges ?? []),
    pendingChange,
  ];
};

const validateComponentValueAndDisplayErrorAndStorePreviousValueIfInvalid = (
  event: CellValueChangedEvent<APIComponentAttributes, InventoryContext>
) => {
  const columnContext = event.colDef.context as ColumnDefinitionArdoqMetadata;
  if (columnContext.inventoryColumnType === 'reference') {
    // Reference columns are handled in a special way in ReferenceCellEditor
    return false;
  }
  const fieldName = event.colDef.field || '';

  const validationResult = validateComponent({
    newValue: event.newValue,
    component: event.data,
    fieldName: fieldName,
    context: event.context,
  });
  const node = event.node as ExtendedRowNode;
  if (validationResult.isValid && node._hasError) {
    clearCellErrorAndRedrawRow(node, fieldName, event.api);
  }
  if (!validationResult.isValid) {
    setHasErrorForCellAndRedrawRow(
      node,
      fieldName,
      event.api,
      validationResult.errorMessages.join(' ')
    );

    storeOriginalValuesToKeepProperOldValuesForCorrectingInvalidCells(
      node,
      fieldName,
      event.oldValue
    );
    node._savingStatus = undefined;
  }

  return validationResult.isValid;
};

/**
 * Reference and tag cells derive their displayed value from external data
 * (component.derivedData / scopeData), so AG Grid does not re-run their
 * valueGetter on a `purge: false` refresh. Force a re-render of the edited row
 * once the refresh has settled (modelUpdated), so the new value shows.
 */
const forceRerenderRowsAfterRefresh = (
  api: GridApi<APIComponentAttributes>,
  rowNodes: IRowNode<APIComponentAttributes>[]
) => {
  if (rowNodes.length === 0) {
    return;
  }
  const onModelUpdated = () => {
    api.removeEventListener('modelUpdated', onModelUpdated);
    api.refreshCells({ rowNodes, force: true });
  };
  api.addEventListener('modelUpdated', onModelUpdated);
};

const forceRerenderEditedCellAfterRefresh = (
  event: CellValueChangedEvent<APIComponentAttributes, any, InventoryContext>
) => forceRerenderRowsAfterRefresh(event.api, [event.node]);

const isMultiEditingActive = (
  event: CellValueChangedEvent<APIComponentAttributes, any, InventoryContext>
): boolean => {
  const cellRanges = event.api.getCellRanges();
  const isMultiRangeSelected =
    cellRanges &&
    cellRanges.length > 0 &&
    (cellRanges[0]?.endRow?.rowIndex !== cellRanges[0]?.startRow?.rowIndex ||
      cellRanges[0]?.columns.length > 1);
  return isMultiRangeSelected || isPasteInProgress;
};

const updateComponentValue = async (
  event: CellValueChangedEvent<APIComponentAttributes, any, InventoryContext>
) => {
  const node = event.node as ExtendedRowNode;
  const fieldName = event.colDef.field;
  if (!node.data || !fieldName) {
    return;
  }

  const columnContext = event.colDef.context as ColumnDefinitionArdoqMetadata;
  if (columnContext.inventoryColumnType === 'lucidchart') {
    const lucidchartEvent = event as LucidchartCellValueChangedEvent;
    if (isMultiEditingActive(event)) {
      markNodeAsPendingLucidchartChange(lucidchartEvent);
      return; // saved in bulk when the paste/fill ends
    }

    const editSucceeded =
      await editLucidchartDocumentsSingleComponent(lucidchartEvent);
    if (!editSucceeded) {
      const route = getNodeRoute(event.node, { includeItself: false });
      refreshServerSideForGrid(event.api, { purge: false, route });
    }
    forceRerenderEditedCellAfterRefresh(event);
    return;
  }

  if (columnContext.inventoryColumnType === 'tag') {
    const isValid =
      validateComponentValueAndDisplayErrorAndStorePreviousValueIfInvalid(
        event
      );
    if (!isValid) {
      return;
    }

    if (isMultiEditingActive(event)) {
      markNodeAsPendingTagChange(event);
      return; // saved in bulk when the paste/fill ends
    }

    await saveTags(event);
    forceRerenderEditedCellAfterRefresh(event);
    event.context.commands.refreshHierarchy(); // this makes sure filters are reapplied
    const route = getNodeRoute(event.node, { includeItself: false });
    refreshServerSideForGrid(event.api, { purge: false, route });
    return;
  }

  if (columnContext.inventoryColumnType === 'reference') {
    if (isMultiEditingActive(event)) {
      markNodeAsPendingReferenceChange(event);
      return; // saved in bulk when the paste/fill/delete ends
    }

    const editConfirmed = await editReferencesSingleComponent(event);
    if (!editConfirmed) {
      const route = getNodeRoute(event.node, { includeItself: false });
      refreshServerSideForGrid(event.api, { purge: false, route });
      return;
    }
    // if the new referenced component is not in the current view, we need to refresh the cells to show them when it's done
    forceRerenderEditedCellAfterRefresh(event);
    refreshServerSideForGrid(event.api);
    return;
  }

  const isValid =
    validateComponentValueAndDisplayErrorAndStorePreviousValueIfInvalid(event);
  if (!isValid) {
    return;
  }

  if (createComponentsOperations.isComponentNotPersisted(event.data._id)) {
    return saveNewComponent(event);
  }

  if (cellValueChangedEventOperations.isSpuriousValueChange(event)) {
    return;
  }

  if (isMultiEditingActive(event)) {
    markNodeAsPendingMultiUpdate(event);
    return; // Ignore fill or copy changes, they are handled in multi update
  }

  if (hasRowBeenRemoved(event)) {
    return;
  }

  const { commands } = event.context;
  const response = await commands.partiallyUpdateComponent(
    event.data._id,
    cellValueChangedEventOperations.getValuesForPartialUpdate(event)
  );

  if (isArdoqError(response)) {
    setHasErrorForCellAndRedrawRow(
      node,
      fieldName,
      event.api,
      formatApiErrorWithTraceId(response, event.context.i18nLocale)
    );
    storeOriginalValuesToKeepProperOldValuesForCorrectingInvalidCells(
      node,
      cellValueChangedEventOperations.getFieldName(event)!,
      event.oldValue
    );
    return;
  }

  mergeScopeDataIntoInventoryContext(
    event.api,
    dateRangeOperations.getMergedDateRangeFieldForScopeData(response.scopeData)
  );

  const updatedContext = event.api.getGridOption('context') as InventoryContext;
  const updatedScopeData = updatedContext.scopeData;

  const updatedComponent =
    reportComponentRowsOperations.mapComponentFromApiComponentFormat(
      response.component,
      updatedScopeData,
      node.data
    );

  node.updateData(updatedComponent);

  clearCellErrorAndRedrawRow(node, fieldName, event.api);
  clearOriginalComponent(node);
  if (fieldName === 'typeId') {
    event.api.refreshCells({ force: true, rowNodes: [node] });
  }
  if (fieldName === 'name') {
    event.api.refreshCells({ force: true, columns: ['parent'] });
  }
  if (fieldName === 'parent' && updatedContext.hierarchy.isHierarchyEnabled) {
    // If the parent has changed, we should refetch to update the hierarchy in scopeData
    updatedContext.commands.refreshHierarchy();
    hierarchyInAgGridOperations.captureExpandedNodesForReexpansion(event.api);
    if (event.newValue) {
      // When the new parent is visible, expand it so the moved component is visible after refresh.
      hierarchyInAgGridOperations.markNodeForReexpansion(
        updatedContext.hierarchy,
        event.newValue
      );
    }
    const sourceRoute = getNodeRoute(event.node, { includeItself: false });
    const newParentNode = event.newValue
      ? (event.api.getRowNode(event.newValue) ?? null)
      : null;
    const targetRoute = newParentNode
      ? getNodeRoute(newParentNode, {
          includeItself: newParentNode.group ?? false,
        })
      : []; // new parent not visible in grid, or moving to root — refresh from root
    deduplicateRoutes([sourceRoute, targetRoute]).forEach(route =>
      refreshServerSideForGrid(event.api, { purge: true, route })
    );
  }
};

/**
 * Store the original field value (from event.oldValue) to send proper oldValue to the backend.
 * If you set invalid value and then fix it in a subsequent edit, we want to send the original value as oldValue,
 * not the last invalid one.
 *
 * Uses event.oldValue which contains the actual previous value before AG Grid modified node.data.
 */
const storeOriginalValuesToKeepProperOldValuesForCorrectingInvalidCells = (
  node: ExtendedRowNode,
  fieldName: string,
  oldValue: unknown
) => {
  if (!node._originalValues) {
    node._originalValues = {};
  }
  // Only store if we haven't stored this field yet (first edit of this field) in case of multiple invalid edits
  if (!(fieldName in node._originalValues)) {
    node._originalValues[fieldName] = oldValue;
  }
};

/**
 * Clear stored original field values after successful update - we're back in sync with the backend.
 */
const clearOriginalComponent = (node: ExtendedRowNode) => {
  node._originalValues = undefined;
};

const getOnFilterChanged = ({
  api,
  context,
}: FilterChangedEvent<APIComponentAttributes, InventoryContext>) => {
  hierarchyInAgGridOperations.clearCollapsedHierarchyModeIfFilterChanged(
    context.hierarchy,
    api.getFilterModel() as FilterModel
  );
  context.commands.notifyFilterChanged(
    mapFilterModelToSubquery(
      api.getFilterModel(),
      columnOperations.toLeafColumnDefs(api.getColumnDefs() ?? [])
    )
  );
};

const setSelectedComponentAndRefresh = (
  params: SelectionChangedEvent<APIComponentAttributes, InventoryContext>
) => {
  const { commands, scopeData } = params.context;
  const gridApi = params.api;
  commands.setSelectedComponents(
    inventoryGridAPIOperations.getSelectedComponents(gridApi, scopeData)
  );
  // Only refresh the selection column instead of all cells for better performance
  gridApi.refreshCells({
    force: true,
    columns: [AG_GRID_SELECTION_COLUMN_ID],
  });
};

const onUnsortSetSortToNameColumn = ({
  columns,
  api,
  source,
}: SortChangedEvent<APIComponentAttributes>) => {
  if (source === 'gridOptionsChanged') return;
  const isSorted = columns?.some(column => column.getSort());
  if (!isSorted) {
    // reset sort to name column
    api.applyColumnState({
      state: [columnOperations.getDefaultSort()],
      defaultState: { sort: null },
    });
  }
};

const onSortChanged = (
  event: SortChangedEvent<APIComponentAttributes, InventoryContext>
) => {
  onUnsortSetSortToNameColumn(event);
  event.context.commands.notifyColumnSortChanged(event);
};

const clearPendingChangesAndErrorsFromNode = (
  rowNode: ExtendedRowNode,
  gridApi: GridApi<APIComponentAttributes>
) => {
  if (rowNode._pendingChanges) {
    rowNode._pendingChanges = undefined;
    if (rowNode._hasError) {
      clearAllCellErrorsAndRedrawRow(rowNode, gridApi);
    }
  }
};

const removePendingChangesAndClearErrorsFromNodes = (
  gridApi: GridApi<APIComponentAttributes>
) => {
  gridApi.forEachNode(node => {
    clearPendingChangesAndErrorsFromNode(node as ExtendedRowNode, gridApi);
  });

  // Newly created components are in pinned top rows,
  // and they can also have pending changes if user edited them
  // after saving them with by providing name, but `api.forEachNode` does not iterate on those rows
  const pinnedTopRowCount = gridApi.getPinnedTopRowCount();
  for (let i = 0; i < pinnedTopRowCount; i++) {
    const node = gridApi.getPinnedTopRow(i);
    if (
      node?.data &&
      !createComponentsOperations.isComponentNotPersisted(node.data._id)
    ) {
      clearPendingChangesAndErrorsFromNode(node as ExtendedRowNode, gridApi);
    }
  }

  clearPendingTagChangesFromNodes(gridApi);
  clearPendingReferenceChangesFromNodes(gridApi);
};

const revertOptimisticBulkChanges = (
  api: GridApi<APIComponentAttributes>,
  inventoryContext: InventoryContext
) => {
  inventoryContext.commands.refreshHierarchy();
  // We can't use deduplicateRoutes here because purge:false doesn't cascade so
  // each block needs its own refresh. In flat mode all routes are [], so this
  // dedupes to a single root-block refresh.
  const seenRoutes = new Set<string>();
  const revertedNodes: IRowNode<APIComponentAttributes>[] = [];
  api.forEachNode(node => {
    if (
      (node as ExtendedRowNode)._pendingChanges ||
      (node as ExtendedRowNode)._pendingTagChange ||
      (node as ExtendedRowNode)._pendingReferenceChanges ||
      (node as ExtendedRowNode)._pendingLucidchartChanges
    ) {
      revertedNodes.push(node);
      const route = getNodeRoute(node, { includeItself: false });
      const key = route.join('\0');
      if (!seenRoutes.has(key)) {
        seenRoutes.add(key);
        refreshServerSideForGrid(api, { purge: false, route });
      }
    }
  });
  forceRerenderRowsAfterRefresh(api, revertedNodes);
  clearPendingLucidchartChangesFromNodes(api);
  removePendingChangesAndClearErrorsFromNodes(api);
};

/**
 * Small updates are fast enough to not show a spinner, but multi updates can take longer.
 */
const multiUpdateComponentsWithSettingSpinnerInTimeout = async (
  multiUpdatePayloads: MultiUpdatePayloads,
  resolvedReferenceChanges: ResolvedReferenceChanges,
  inventoryContext: InventoryContext,
  api: GridApi<APIComponentAttributes>,
  eventSource: BulkEditEventSource
) => {
  const timeoutId = setTimeout(() => {
    api.setGridOption('loading', true);
  }, 200);

  const result = await multiUpdateComponents(
    multiUpdatePayloads.partialBulkUpdates,
    inventoryContext,
    api,
    eventSource
  );
  if (isArdoqError(result)) {
    clearTimeout(timeoutId);
    api.setGridOption('loading', false);
    return;
  }

  await multiUpdateTags(
    multiUpdatePayloads.pendingTagChanges,
    inventoryContext,
    api
  );

  const shouldRefreshForReferences = await multiUpdateReferences(
    resolvedReferenceChanges,
    inventoryContext,
    api,
    eventSource
  );
  const shouldRefreshForLucidchart = await multiUpdateLucidchartDocuments(
    multiUpdatePayloads.pendingLucidchartChanges,
    inventoryContext,
    api,
    eventSource
  );
  if (shouldRefreshForReferences || shouldRefreshForLucidchart) {
    refreshServerSideForGrid(api);
  }

  clearTimeout(timeoutId);
  api.setGridOption('loading', false);
};

const multiUpdateComponents = async (
  componentsToUpdate: PartialBulkUpdateValues,
  inventoryContext: InventoryContext,
  api: GridApi<APIComponentAttributes>,
  eventSource: MultiUpdateEventSource
): Promise<APIComponentAttributes[] | ArdoqError> => {
  if (componentsToUpdate.length === 0) {
    return Promise.resolve([]);
  }
  const { commands, scopeData } = inventoryContext;

  const result = await commands.multiUpdateComponents(
    componentsToUpdate,
    eventSource
  );

  if (isArdoqError(result)) {
    revertOptimisticBulkChanges(api, inventoryContext);
    const i18nLocale = inventoryContext.i18nLocale;
    const traceId = getArdoqErrorTraceId(result);
    confirm(getConfirmMultiUpdateFailedDialog(i18nLocale, result, traceId));
    return result;
  }

  for (const component of result) {
    const rowNode = api.getRowNode(component._id);
    const componentWithMergedDateRangeFieldsAndSkippedOrder =
      reportComponentRowsOperations.mapComponentFromApiComponentFormat(
        component,
        scopeData,
        rowNode?.data
      );
    rowNode?.updateData(componentWithMergedDateRangeFieldsAndSkippedOrder);
  }
  removePendingChangesAndClearErrorsFromNodes(api);
  return result;
};

/**
 * Extracts partial bulk update values from nodes pending multi-update.
 * For each component, collects only the changed fields with their old and new values.
 * If the same field was changed multiple times (e.g., paste then fill), uses the earliest old value
 * and the latest new value to ensure proper conflict detection.
 *
 * Returns an array where each element contains a componentId and an array of field updates for that component.
 *
 * @returns Array of partial bulk update values with componentId and array of field updates per component
 */
const collectPendingChangesFromNode = (
  node: IRowNode<APIComponentAttributes>,
  partialBulkUpdates: PartialBulkUpdateValues
): void => {
  const extendedNode = node as ExtendedRowNode;
  if (!node.data || !extendedNode._pendingChanges) {
    return;
  }
  // Deduplicate changes by field name:
  // If a field was changed multiple times, keep the first oldValue and last newValue
  const changesByField = new Map<
    string,
    { oldValue: unknown; newValue: unknown; field: string }
  >();

  for (const change of extendedNode._pendingChanges) {
    const existing = changesByField.get(change.field);
    if (existing) {
      changesByField.set(change.field, {
        oldValue: existing.oldValue,
        newValue: change.newValue,
        field: change.field,
      });
    } else {
      changesByField.set(change.field, change);
    }
  }

  const updates = Array.from(changesByField.values());
  partialBulkUpdates.push({
    componentId: node.data._id,
    updates,
  });
};

/**
 * Collects all update payloads that can happen
 */
type MultiUpdatePayloads = {
  partialBulkUpdates: PartialBulkUpdateValues;
  pendingTagChanges: PendingTagChange[];
  pendingReferenceChanges: PendingReferenceChange[];
  pendingLucidchartChanges: PendingLucidchartChange[];
};

const getPartialComponentsToUpdate = (
  api: GridApi<APIComponentAttributes>
): PartialBulkUpdateValues => {
  const partialBulkUpdates: PartialBulkUpdateValues = [];

  api.forEachNode(node =>
    collectPendingChangesFromNode(node, partialBulkUpdates)
  );

  // Newly created components are in pinned top rows,
  // and they can also have pending changes if user edited them
  // after saving them with by providing name, but `api.forEachNode` does not iterate on those rows
  const pinnedTopRowCount = api.getPinnedTopRowCount();
  for (let i = 0; i < pinnedTopRowCount; i++) {
    const node = api.getPinnedTopRow(i);
    if (
      node?.data &&
      !createComponentsOperations.isComponentNotPersisted(node.data._id)
    ) {
      collectPendingChangesFromNode(node, partialBulkUpdates);
    }
  }

  return partialBulkUpdates;
};

const getMultiUpdatePayloads = (
  api: GridApi<APIComponentAttributes>
): MultiUpdatePayloads => {
  const partialBulkUpdates = getPartialComponentsToUpdate(api);
  const pendingTagChanges = collectPendingTagChangesFromNodes(api);
  const pendingReferenceChanges = collectPendingReferenceChangesFromNodes(api);
  const pendingLucidchartChanges =
    collectPendingLucidchartChangesFromNodes(api);

  return {
    partialBulkUpdates,
    pendingTagChanges,
    pendingReferenceChanges,
    pendingLucidchartChanges,
  };
};

const onFillEnd = async (
  event: FillEndEvent<APIComponentAttributes, InventoryContext>
) => {
  const { api, context } = event;

  const multiUpdatePayloads = getMultiUpdatePayloads(api);

  await displayConfirmationDialogAndUpdateComponentsOrRevertChanges(
    multiUpdatePayloads,
    context,
    api,
    'fill'
  );
};

const displayConfirmationDialogAndUpdateComponentsOrRevertChanges = async (
  multiUpdatePayloads: MultiUpdatePayloads,
  inventoryContext: InventoryContext,
  api: GridApi<APIComponentAttributes>,
  eventSource: BulkEditEventSource
) => {
  const {
    pendingTagChanges,
    partialBulkUpdates,
    pendingReferenceChanges,
    pendingLucidchartChanges,
  } = multiUpdatePayloads;
  const componentsToUpdateCount = uniq([
    ...pendingTagChanges.map(it => it.entityId),
    ...partialBulkUpdates.map(it => it.componentId),
    ...pendingReferenceChanges.map(it => it.componentId),
    ...pendingLucidchartChanges.map(it => it.componentId),
  ]).length;
  if (componentsToUpdateCount === 0) {
    return;
  }

  // resolved before the dialog so it can state how many references actually go
  const scopeData = await loadScopeDataForPendingReferences(
    pendingReferenceChanges,
    api,
    inventoryContext
  );
  const resolvedReferenceChanges = resolveReferenceChanges(
    pendingReferenceChanges,
    { ...inventoryContext, scopeData },
    { enforceTriples: true }
  );
  const {
    referenceIdsToDelete,
    referencesToCreate,
    blockedDeleteCount,
    skippedNoTripleCount,
    unloadedComponentCount,
    blockedCreateCount,
  } = resolvedReferenceChanges;

  // a range can span reference and ordinary columns, so both have to be named
  const fieldUpdateComponentCount = uniq([
    ...pendingTagChanges.map(it => it.entityId),
    ...partialBulkUpdates.map(it => it.componentId),
    ...pendingLucidchartChanges.map(it => it.componentId),
  ]).length;

  const hasSomethingToSend =
    referenceIdsToDelete.length > 0 ||
    referencesToCreate.length > 0 ||
    fieldUpdateComponentCount > 0;

  if (!hasSomethingToSend) {
    const hasUnchangedReferencesToExplain =
      blockedDeleteCount > 0 ||
      skippedNoTripleCount > 0 ||
      unloadedComponentCount > 0 ||
      blockedCreateCount > 0;
    if (hasUnchangedReferencesToExplain) {
      await confirm(
        getNothingChangedDialog({
          blockedDeleteCount,
          skippedNoTripleCount,
          unloadedComponentCount,
          blockedCreateCount,
          i18nLocale: inventoryContext.i18nLocale,
        })
      );
    }
    revertOptimisticBulkChanges(api, inventoryContext);
    return;
  }

  // A paste is applied in full or not at all: creating the references that
  // happen to be allowed, while dropping the rest, leaves the user with a
  // partial result they did not ask for and cannot see. A blocked delete counts
  // too, since a paste replaces the cell — but only when the operation creates
  // something, so pressing delete over a range still removes what it can.
  const hasRejectedCreate =
    skippedNoTripleCount > 0 ||
    unloadedComponentCount > 0 ||
    blockedCreateCount > 0;
  const isPasteWithBlockedDelete =
    blockedDeleteCount > 0 && referencesToCreate.length > 0;
  if (hasRejectedCreate || isPasteWithBlockedDelete) {
    await confirm(
      getNothingChangedDialog({
        blockedDeleteCount: isPasteWithBlockedDelete ? blockedDeleteCount : 0,
        skippedNoTripleCount,
        unloadedComponentCount,
        blockedCreateCount,
        i18nLocale: inventoryContext.i18nLocale,
      })
    );
    revertOptimisticBulkChanges(api, inventoryContext);
    return;
  }

  const isChangingReferences =
    referenceIdsToDelete.length > 0 ||
    referencesToCreate.length > 0 ||
    blockedDeleteCount > 0;

  const confirmationDialog = isChangingReferences
    ? getBulkReferenceDeleteConfirmationDialog({
        createCount: referencesToCreate.length,
        deleteCount: referenceIdsToDelete.length,
        componentCount: resolvedReferenceChanges.componentCount,
        blockedDeleteCount,
        fieldUpdateComponentCount,
        i18nLocale: inventoryContext.i18nLocale,
      })
    : // skip confirmation for just one row updates
      componentsToUpdateCount > 1 &&
      getMultiUpdateConfirmationDialog(
        componentsToUpdateCount,
        inventoryContext.i18nLocale
      );

  if (confirmationDialog) {
    const confirmed = await confirm(confirmationDialog);
    if (!confirmed) {
      revertOptimisticBulkChanges(api, inventoryContext);
      return;
    }
  }

  await multiUpdateComponentsWithSettingSpinnerInTimeout(
    multiUpdatePayloads,
    resolvedReferenceChanges,
    inventoryContext,
    api,
    eventSource
  );
};

let isPasteInProgress = false;

const onPasteStart = async (
  event: PasteStartEvent<APIComponentAttributes, InventoryContext>
) => {
  isPasteInProgress = true;
  clearPendingComponentNamesFromNodes(event.api);
};

const onPasteEnd = async (
  event: PasteEndEvent<APIComponentAttributes, InventoryContext>
) => {
  const { api, context } = event;

  const {
    scopeData,
    unmatchedNameCount,
    ambiguousNameCount,
    ineligibleParentCount,
    lookupError,
  } = await resolvePastedComponentNames(api, context);

  // a paste is saved whole or not at all, so a name we cannot pin down stops
  // the ids that came with it too
  if (lookupError) {
    await showEditReferencesFailedDialog(context.i18nLocale, lookupError);
    revertOptimisticBulkChanges(api, context);
    isPasteInProgress = false;
    return;
  }
  if (
    unmatchedNameCount > 0 ||
    ambiguousNameCount > 0 ||
    ineligibleParentCount > 0
  ) {
    await confirm(
      getUnresolvedNamesDialog({
        unmatchedNameCount,
        ambiguousNameCount,
        ineligibleParentCount,
        i18nLocale: context.i18nLocale,
      })
    );
    revertOptimisticBulkChanges(api, context);
    isPasteInProgress = false;
    return;
  }

  await displayConfirmationDialogAndUpdateComponentsOrRevertChanges(
    getMultiUpdatePayloads(api),
    // the components the names resolved to reach the flush through the scope
    // data, which the event captured before they arrived
    { ...context, scopeData },
    api,
    'paste'
  );

  isPasteInProgress = false;
};

const processDataFromClipboard = (
  params: ProcessDataFromClipboardParams<
    APIComponentAttributes,
    InventoryContext
  >
) => {
  const commands = params.context.commands;
  const data = params.data.map(row => row.map(fromClipboardCellText));
  if (data.length === 0) {
    return data;
  }

  const rowsLimit = 100;

  if (data.length > rowsLimit) {
    const i18nLocale = params.context.i18nLocale;
    confirm({
      title: t(
        i18nLocale,
        'inventory:pasteSizeLimit.title',
        'Paste Size Limit'
      ),
      text: t(
        i18nLocale,
        'inventory:pasteSizeLimit.text',
        { rowCount: data.length, limit: rowsLimit },
        `Your clipboard contains ${data.length} rows, but we can only paste ${rowsLimit} at a time. For larger datasets, please use Excel import.`
      ),
      cancelButtonTitle: '',
      confirmButtonTitle: t(i18nLocale, 'inventory:dialogs.ok', 'OK'),
    });
    commands.notifyClipboardDataTooLarge(data.length);

    return [];
  }

  if (data.length === 1 && data[0].length === 1 && data[0][0] === '') {
    return data; // do nothing if only one empty cell is pasted
  }

  // Excel uses format which adds an empty row into grid, which cause unintentional changes and can lower data quality.
  // This is known issue in AG Grid that needs special handling.
  const lastRow = data[data.length - 1];
  const isLastRowEmpty = lastRow.every(
    cell => cell === null || cell === undefined || cell.toString().trim() === ''
  );
  if (isLastRowEmpty) {
    return data.slice(0, -1);
  }
  return data;
};

// Not a valueFormatter: that also renders the cell, so the quotes would show in the grid.
const processCellForClipboard = (
  params: ProcessCellForExportParams<APIComponentAttributes, InventoryContext>
) => {
  const text = params.formatValue(params.value);
  if (params.type === 'dragCopy' || typeof text !== 'string') {
    return text;
  }
  return toClipboardCellText(text);
};

const syncSelectionColumnPinnedWithName = (
  api: GridApi<APIComponentAttributes>
): void => {
  const selectionColumn = api.getColumn(AG_GRID_SELECTION_COLUMN_ID);
  if (!selectionColumn) {
    return;
  }
  const targetPinned = api.getColumn('name')?.getPinned() ?? null;
  if (selectionColumn.getPinned() === targetPinned) {
    return;
  }
  api.setColumnsPinned([AG_GRID_SELECTION_COLUMN_ID], targetPinned);
};

const syncPinningNameWithDeleteComponentColumn = (
  event: ColumnPinnedEvent<APIComponentAttributes>
) => {
  if (event.column?.getColId() === 'name') {
    const gridApi = event.api;
    const nameColumnPinned = event.column.getPinned();
    const deleteComponentColumn = gridApi.getColumn(deleteComponentColumnId);
    if (deleteComponentColumn) {
      gridApi.setColumnsPinned([deleteComponentColumnId], nameColumnPinned);
    }
  }
};

export type VisibleColumn = { key: string; type: ReportColumnType };

const hasNewReferenceColumnsPred = (
  event: ColumnMenuVisibleChangedEvent,
  previousVisibleColumns: VisibleColumn[]
): boolean => {
  const previousVisibleReferenceColumnKeys = previousVisibleColumns
    .filter(
      column =>
        column.type === ReportColumnType.REFERENCE_TYPE_OUTGOING ||
        column.type === ReportColumnType.REFERENCE_TYPE_INCOMING
    )
    .map(column => column.key);

  const currentVisibleReferenceColumnIds = event.api
    .getAllDisplayedColumns()
    .filter(col => {
      const colDef = col.getColDef();
      return (
        (colDef.context as ColumnDefinitionArdoqMetadata | undefined)
          ?.inventoryColumnType === 'reference'
      );
    })
    .map(col => col.getColId());

  return currentVisibleReferenceColumnIds.some(
    colId => !previousVisibleReferenceColumnKeys.includes(colId)
  );
};

const handleColumnMenuVisibilityChanged = (
  event: ColumnMenuVisibleChangedEvent,
  previousVisibleColumns: VisibleColumn[]
) => {
  if (event.visible) {
    return;
  }

  const hasNewReferenceColumns = hasNewReferenceColumnsPred(
    event,
    previousVisibleColumns
  );

  if (hasNewReferenceColumns) {
    refreshServerSideForGrid(event.api, undefined, {
      hierarchyRefreshMode: 'inPlace',
    });
  }
};

const getRowElementByRowId = (api: GridApi, rowId: string): Element | null => {
  const rowIndex = api.getRowNode(rowId)?.rowIndex;
  if (rowIndex === null || rowIndex === undefined) {
    return null;
  }
  return document.querySelector(`[row-id="${rowId}"]`);
};

const removeAllDragOverHighlights = () => {
  document.querySelectorAll('.ag-row-drag-over').forEach(element => {
    element.classList.remove('ag-row-drag-over');
  });
};

const addHighlightToTheRowUnderCurrentMousePosition = (
  event: RowDragEvent<APIComponentAttributes, InventoryContext>
) => {
  const targetNode = event.overNode;
  if (!targetNode || !targetNode.data) {
    return;
  }
  const rowElement = getRowElementByRowId(event.api, targetNode.data._id);
  if (rowElement) {
    rowElement.classList.add('ag-row-drag-over');
  }
};

const handleRowDragMove = (
  event: RowDragEvent<APIComponentAttributes, InventoryContext>
) => {
  removeAllDragOverHighlights();
  addHighlightToTheRowUnderCurrentMousePosition(event);
};

const handleRowDragLeave = () => {
  removeAllDragOverHighlights();
};

export const SESSION_KEY_MOVE_HIERARCHY = '[inventory] MOVE_HIERARCHY_CONFIRM';

const confirmMove = async (
  movedNodes: ExtendedRowNode[],
  targetNode: ExtendedRowNode,
  i18nLocale: InventoryContext['i18nLocale'],
  scopeData: EnhancedScopeData
): Promise<boolean> => {
  if (window.sessionStorage.getItem(SESSION_KEY_MOVE_HIERARCHY) === 'true') {
    return true;
  }

  const targetName = targetNode.data?.name ?? '';
  const count = movedNodes.length;
  const isSingular = count === 1;

  const result = await confirm({
    title: t(
      i18nLocale,
      isSingular
        ? 'inventory:moveComponents.title.single'
        : 'inventory:moveComponents.title.plural',
      { count },
      isSingular ? 'Move component?' : `Move ${count} components?`
    ),
    text: getMoveComponentsDialogText({
      movedNodes,
      targetName,
      scopeData,
      i18nLocale,
    }),
    confirmButtonTitle: t(
      i18nLocale,
      'inventory:moveComponents.confirmButtonTitle',
      { count },
      isSingular ? 'Move component' : `Move ${count} components`
    ),
    modalSize: ModalSize.S,
    cancelButtonTitle: t(
      i18nLocale,
      'inventory:confirmDialogs.cancel',
      'Cancel'
    ),
    showRememberActionCheckbox: true,
    rememberActionCustomCheckboxText: t(
      i18nLocale,
      'inventory:moveComponents.dontAskAgain',
      `Don't show this message again.`
    ),
  });

  if (!isConfirmResult(result)) {
    return false;
  }

  if (result.rememberAction) {
    window.sessionStorage.setItem(SESSION_KEY_MOVE_HIERARCHY, 'true');
  }
  return result.confirmed;
};

const handleRowDragEnd = async (
  event: RowDragEvent<APIComponentAttributes, InventoryContext>
) => {
  const { commands } = event.context;
  const movedNodes = event.nodes as ExtendedRowNode[];
  const targetNode = event.overNode as ExtendedRowNode;
  if (!targetNode || !targetNode.data || !targetNode.data._id) {
    return;
  }
  const newParentComponentId = targetNode.data._id;

  removeAllDragOverHighlights();

  if (event.nodes.length === 0 || movedNodes.some(node => !node.data)) {
    return;
  }

  const targetWorkspaceId = targetNode.data.rootWorkspace;
  if (!event.context.accessControl.canEditWorkspace(targetWorkspaceId)) {
    return;
  }

  if (
    movedNodes.some(movedNode => movedNode.data!._id === newParentComponentId)
  ) {
    return; // don't do anything if the component is moved on itself
  }

  for (const movedNode of movedNodes) {
    const validationResult = validateComponent({
      component: movedNode.data!,
      fieldName: 'parent',
      newValue: newParentComponentId,
      context: event.context,
    });
    if (!validationResult.isValid) {
      const i18nLocale = event.context.i18nLocale;
      await confirm(
        getConfirmCannotMoveComponentsDialog(i18nLocale, validationResult)
      );
      return;
    }
  }

  const confirmed = await confirmMove(
    movedNodes,
    targetNode,
    event.context.i18nLocale,
    event.context.scopeData
  );
  if (!confirmed) {
    return;
  }

  const componentsInApiFormat: PartialBulkUpdateValues = event.nodes.map(
    node => {
      const data = node.data as APIComponentAttributes;
      return {
        componentId: data._id,
        updates: [
          {
            oldValue: data.parent || null,
            newValue: newParentComponentId || null,
            field: 'parent',
          },
        ],
      };
    }
  );
  const response = await commands.multiUpdateComponents(
    componentsInApiFormat,
    'drag-and-drop-parent'
  );

  if (isArdoqError(response)) {
    const errorMessage = formatApiErrorWithTraceId(
      response,
      event.context.i18nLocale
    );
    movedNodes.forEach(movedNode =>
      setHasErrorForCellAndRedrawRow(
        movedNode,
        'parent',
        event.api,
        errorMessage
      )
    );
    return;
  }

  event.api.deselectAll();

  hierarchyInAgGridOperations.captureExpandedNodesForReexpansion(event.api);
  hierarchyInAgGridOperations.markNodeForReexpansion(
    event.context.hierarchy,
    newParentComponentId
  );
  refreshServerSideForGrid(event.api, undefined, {
    hierarchyRefreshMode: 'purgeWithoutExpansionCapture',
  });
};

/**
 * AG Grid derives the number of header rows from the provided column tree, so
 * a field group whose every column is hidden still adds a group header row.
 */
const syncGroupHeaderHeightWithDisplayedGroups = ({
  api,
}: Pick<DisplayedColumnsChangedEvent<APIComponentAttributes>, 'api'>) => {
  const groupHeaderHeight = columnOperations.hasDisplayedColumnInGroup(
    api.getAllDisplayedColumns()
  )
    ? undefined
    : 0;
  if (api.getGridOption('groupHeaderHeight') === groupHeaderHeight) {
    return;
  }
  api.setGridOption('groupHeaderHeight', groupHeaderHeight);
};

const handleDisplayedColumnsChanged = (
  event: DisplayedColumnsChangedEvent<APIComponentAttributes>
) => {
  syncGroupHeaderHeightWithDisplayedGroups(event);
  syncSelectionColumnPinnedWithName(event.api);
};

export const AgEventHandlers = {
  updateComponentValue,
  getOnFilterChanged,
  setSelectedComponentAndRefresh,
  onUnsortSetSortToNameColumn,
  onSortChanged,
  onCellKeyDown,
  onFillEnd,
  onPasteStart,
  onPasteEnd,
  processDataFromClipboard,
  processCellForClipboard,
  syncPinningNameWithDeleteComponentColumn,
  syncSelectionColumnPinnedWithName,
  handleColumnMenuVisibilityChanged,
  syncGroupHeaderHeightWithDisplayedGroups,
  handleDisplayedColumnsChanged,
  handleRowDragMove,
  handleRowDragLeave,
  handleRowDragEnd,
  getMultiUpdatePayloads,
};
